import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import { logError } from '../../utils/logger';

// MUST use userPool auth mode for admin operations
const client = generateClient({ authMode: 'userPool' }) as any;

type FilterType = 'all' | 'admins' | 'non-admins';

interface PortalMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  memberNumber: string | null;
  cognitoSub: string;
  isAdmin: boolean;
}

const RoleManagement: React.FC = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState<PortalMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminStatusWarning, setAdminStatusWarning] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'promote' | 'remove';
    member: PortalMember;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // #157 (S1): rows skipped by loadData because they came back null with a
  // GraphQL error (missing required field) — shown as an amber notice
  // rather than aborting the load.
  const [unreadableCount, setUnreadableCount] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setAdminStatusWarning(false);

      // Fetch all members with portal accounts (non-null cognitoSub).
      // #79-class: a filtered list() caps rows SCANNED, not returned, so a
      // single call could silently drop members from role management past
      // the first page. Page fully.
      //
      // #157 (S1): a row missing a required field comes back as null with
      // a GraphQL error rather than throwing. Keep paging, skip the null
      // rows (counted for the amber notice below), and never abort the
      // whole load on their account.
      let allMembers: any[] = [];
      const memberErrors: any[] = [];
      let unreadable = 0;
      let membersToken: string | null | undefined;
      do {
        const page: any = await client.models.Member.list({
          filter: { cognitoSub: { attributeExists: true } },
          nextToken: membersToken || undefined,
        });
        const items: any[] = page.data || [];
        const readable = items.filter((m) => m && typeof m === 'object');
        unreadable += items.length - readable.length;
        if (page.errors?.length) {
          memberErrors.push(...page.errors);
          logError('RoleManagement', page.errors);
        }
        allMembers = allMembers.concat(readable);
        membersToken = page.nextToken;
      } while (membersToken);

      setUnreadableCount(unreadable);

      // Fetch admin subs from Cognito via custom query
      let adminSubs: string[] = [];
      try {
        const { data: adminResult } = await client.queries.listAdminUsers({});
        const parsed = typeof adminResult === 'string' ? JSON.parse(adminResult) : adminResult;
        if (parsed?.success && parsed.adminSubs) {
          adminSubs = parsed.adminSubs;
        } else if (parsed?.error) {
          logError('RoleManagement', parsed.error);
          setAdminStatusWarning(true);
        }
      } catch (err) {
        logError('RoleManagement', err);
        setAdminStatusWarning(true);
      }

      // Cross-reference members with admin status
      const portalMembers: PortalMember[] = (allMembers || [])
        .filter((m: any) => m.cognitoSub)
        .map((m: any) => ({
          id: m.id,
          firstName: m.firstName || '',
          lastName: m.lastName || '',
          email: m.email || '',
          memberNumber: m.memberNumber || null,
          cognitoSub: m.cognitoSub,
          isAdmin: adminSubs.includes(m.cognitoSub),
        }));

      setMembers(portalMembers);
    } catch (err) {
      console.error('Error loading role management data:', err);
      setError('Failed to load role management data');
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (member: PortalMember) => {
    setActionLoading(member.id);
    setConfirmAction(null);
    try {
      const { data: result } = await client.mutations.promoteAdmin({
        cognitoSub: member.cognitoSub,
      });
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      if (parsed?.success) {
        setSuccessMessage(`Successfully promoted ${member.firstName} ${member.lastName} to admin`);
        await loadData();
      } else {
        setErrorMessage(parsed?.error || 'Failed to promote member to admin');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to promote member to admin');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (member: PortalMember) => {
    setActionLoading(member.id);
    setConfirmAction(null);
    try {
      const { data: result } = await client.mutations.removeAdmin({
        cognitoSub: member.cognitoSub,
      });
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      if (parsed?.success) {
        setSuccessMessage(`Successfully removed admin access from ${member.firstName} ${member.lastName}`);
        await loadData();
      } else {
        setErrorMessage(parsed?.error || 'Failed to remove admin access');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to remove admin access');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredMembers = members.filter((m) => {
    if (filter === 'admins') return m.isAdmin;
    if (filter === 'non-admins') return !m.isAdmin;
    return true;
  });

  // Get current user's sub to prevent self-demotion in UI
  const currentUserEmail = user?.email || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fishing-blue"></div>
        <span className="ml-3 text-gray-600">Loading role management...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadData}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Role Management</h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage admin access for club members with portal accounts.
        </p>
      </div>

      {/* #157 (S1): malformed-row tolerance notice. */}
      {unreadableCount > 0 && (
        <div role="status" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {unreadableCount === 1 ? '1 unreadable record' : `${unreadableCount} unreadable records`} were skipped.
        </div>
      )}

      {/* Toast Messages */}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Admin Status Warning */}
      {adminStatusWarning && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Unable to load admin status from Cognito. Admin badges may not be accurate.
          </p>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="mb-4 flex space-x-4">
        {([
          { key: 'all' as FilterType, label: 'All Portal Members' },
          { key: 'admins' as FilterType, label: 'Admins Only' },
          { key: 'non-admins' as FilterType, label: 'Non-Admin Members' },
        ]).map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              filter === f.key
                ? 'bg-fishing-blue text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.label}
            <span className="ml-1 text-xs">
              ({f.key === 'all'
                ? members.length
                : f.key === 'admins'
                ? members.filter((m) => m.isAdmin).length
                : members.filter((m) => !m.isAdmin).length})
            </span>
          </button>
        ))}
      </div>

      {/* Members List */}
      {filteredMembers.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          {members.length === 0
            ? 'No members have portal accounts yet.'
            : 'No members match the selected filter.'}
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.map((member) => {
                const isCurrentUser = member.email.toLowerCase() === currentUserEmail.toLowerCase();
                return (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {member.firstName} {member.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.memberNumber || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.isAdmin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Member
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {actionLoading === member.id ? (
                        <span className="text-gray-400">Processing...</span>
                      ) : member.isAdmin ? (
                        !isCurrentUser && (
                          <button
                            onClick={() => setConfirmAction({ type: 'remove', member })}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            Remove Admin
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => setConfirmAction({ type: 'promote', member })}
                          className="text-fishing-blue hover:text-blue-700 font-medium"
                        >
                          Promote to Admin
                        </button>
                      )}
                      {isCurrentUser && member.isAdmin && (
                        <span className="text-xs text-gray-400 ml-2">(You)</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {confirmAction.type === 'promote' ? 'Promote to Admin' : 'Remove Admin Access'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {confirmAction.type === 'promote'
                ? `Are you sure you want to promote ${confirmAction.member.firstName} ${confirmAction.member.lastName} (${confirmAction.member.email}) to admin? They will gain full access to the admin dashboard.`
                : `Are you sure you want to remove admin access from ${confirmAction.member.firstName} ${confirmAction.member.lastName} (${confirmAction.member.email})? They will lose access to the admin dashboard.`}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  confirmAction.type === 'promote'
                    ? handlePromote(confirmAction.member)
                    : handleRemove(confirmAction.member)
                }
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  confirmAction.type === 'promote'
                    ? 'bg-fishing-blue hover:bg-blue-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {confirmAction.type === 'promote' ? 'Promote' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
